/**
 * Every state change goes through a serialisable command, stamped with the tick it applies to.
 * Match commands carry the player who sends them: the rules refuse a command from anyone but the
 * current player (hot-seat today, online at step 6).
 */
export type Command =
  /** Sandbox: fills a disc of cells with material `mat`. */
  | { tick: number; type: 'paint'; x: number; y: number; radius: number; mat: number }
  /** Sandbox: an explosion at a cell, without damage (a debug tool). */
  | { tick: number; type: 'explode'; x: number; y: number; radius: number }
  /** Sandbox: puts a unit of `kind` for `player` with its centre at cell (x, y); it drops to the ground. */
  | { tick: number; type: 'spawn'; player: number; kind: number; x: number; y: number }
  /** Sandbox: drops a chest with `content` (a weapon id or CONTENT_MEDKIT) at the top of column x. */
  | { tick: number; type: 'chest'; x: number; content: number }
  /** Match, placement: the current player puts an invader of `kind` at cell (x, y). */
  | { tick: number; type: 'place'; player: number; kind: number; x: number; y: number }
  /** Match: highlights one of the player's units (click on it, or ◀ ▶). */
  | { tick: number; type: 'select'; player: number; unit: number }
  /** Match: ACTION — locks the highlighted unit for this turn. */
  | { tick: number; type: 'lock'; player: number }
  /** Match: right click — back to the choice of the unit, as long as it has neither moved nor fired. */
  | { tick: number; type: 'unlock'; player: number }
  | { tick: number; type: 'walk'; player: number; unit: number; dir: -1 | 0 | 1 }
  | { tick: number; type: 'jump'; player: number; unit: number }
  /** Puts the jetpack on (uses one): then 'thrust' and the walking keys fly it. Not the turn's shot. */
  | { tick: number; type: 'jetpack'; player: number; unit: number }
  /** Jetpack: the thrust up is held (on) or released. */
  | { tick: number; type: 'thrust'; player: number; unit: number; on: boolean }
  /**
   * The drill (uses one): the unit bores its way in direction `angle` while the button is held ('aim' steers it,
   * 'release' stops it). Not the turn's shot.
   */
  | { tick: number; type: 'drill'; player: number; unit: number; angle: number }
  /** Match: ends the current player's turn now (resolution, then the next player). */
  | { tick: number; type: 'endTurn'; player: number }
  /**
   * Online: the host excluded `player` (author's addition). Sent by the server in its name, never by a browser: its
   * invaders vanish and it plays no more turns.
   */
  | { tick: number; type: 'forfeit'; player: number }
  /** A ghost turn (author's addition): an eliminated player drops a chest over column x, or puts a mine under (x, y). */
  | { tick: number; type: 'ghost'; player: number; action: 'chest' | 'mine'; x: number; y: number }
  /** Flamethrower or drill held: the jet, or the drill, follows the aim. */
  | { tick: number; type: 'aim'; player: number; unit: number; angle: number }
  /** Flamethrower or drill: the button is released, the jet or the drill stops. */
  | { tick: number; type: 'release'; player: number; unit: number }
  /**
   * `angle` in integer degrees (0 = right, 90 = up), `power` 0-100 (ignored by direct-fire weapons);
   * (tx, ty): the designated cell for a 'target' weapon (the meteor).
   */
  | {
    tick: number; type: 'fire'; player: number; unit: number; weapon: number; angle: number; power: number;
    tx?: number; ty?: number;
    /** Voodoo doll: the enemy linked to it. */
    link?: number;
    /** Remote: centre of the square to lift (the destination is (tx, ty)), and its rotation in steps of 15°. */
    sx?: number; sy?: number; rot?: number;
  };

/** Commands of the sandbox tools, refused under match rules. */
export function isSandboxCommand(cmd: Command): cmd is Extract<Command, { type: 'paint' | 'explode' | 'spawn' | 'chest' }> {
  return cmd.type === 'paint' || cmd.type === 'explode' || cmd.type === 'spawn' || cmd.type === 'chest';
}

/** A command before it is stamped with a tick (what input code builds). */
export type CommandInput = { [K in Command['type']]: Omit<Extract<Command, { type: K }>, 'tick'> }[Command['type']];
